import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import Modal from '../ui/Modal';
import { API_BASE_URL } from '../../config/api';
import { useRole } from '../../contexts/RoleContext';
import { getDashboardRouteForRole } from '../../utils/getDashboardRouteForRole';
import BrandSymbol from '../ui/BrandSymbol';
declare global {
  interface Window {
    Tawk_API?: unknown;
  }
}

interface KBArticle {
  id: number;
  title: string;
  slug: string;
  content: string;
  tags: string[] | null;
  categoryId: number | null;
  categoryName: string | null;
}

const Frame199: React.FC = () => {
  const navigate = useNavigate();
  const { activeRole } = useRole();
  const dashboardRoute = getDashboardRouteForRole(activeRole);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [articles, setArticles] = useState<KBArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState<KBArticle | null>(null);

  // Fetch KB articles from database
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/kb-articles`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setArticles(data.data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch KB articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  // Get all unique categories from articles
  const categories = ['all', ...new Set(articles.map(a => a.categoryName).filter(Boolean) as string[])];

  // Get category counts
  const getCategoryCount = (categoryId: string) => {
    if (categoryId === 'all') return articles.length;
    return articles.filter(a => a.categoryName === categoryId).length;
  };

  // Filter articles based on search and category
  const filteredArticles = articles.filter(article => {
    const matchesCategory = selectedCategory === 'all' || article.categoryName === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.content.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get preview text from content
  const getPreview = (content: string, length: number = 100) => {
    const plainText = content.replace(/[#*`[\]()]/g, '').replace(/<[^>]*>/g, '');
    return plainText.length > length ? plainText.substring(0, length) + '...' : plainText;
  };

  const scrollToSection = (sectionId: string) => {
    if (sectionId === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const openTawkTo = () => {
    if (window.Tawk_API && window.Tawk_API.toggle) {
      window.Tawk_API.toggle();
    } else {
      console.warn('Tawk.to is not loaded yet');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <header className="bg-white border-b border-grey-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <BrandSymbol size="3xl" showText={true} variant="dark"/>
            </div>

            <nav className="hidden md:flex space-x-8">
              <button
                onClick={() => scrollToSection('features')}
                className="font-sans text-grey-700 hover:text-blue-900 transition-colors"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection('security')}
                className="font-sans text-grey-700 hover:text-blue-900 transition-colors"
              >
                Security
              </button>
              <button
                onClick={() => scrollToSection('top')}
                className="font-sans text-grey-700 hover:text-blue-900 transition-colors"
              >
                Support
              </button>
            </nav>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(dashboardRoute)}
                className="font-sans text-grey-700 hover:text-blue-900 transition-colors"
              >
                Dashboard
              </button>
              <button
                onClick={() => navigate('/settings')}
                className="font-sans text-grey-700 hover:text-blue-900 transition-colors"
              >
                Profile
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-50 to-green-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-4xl font-bold text-grey-700 mb-4">
            Digital Legacy Support Center
          </h1>
          <p className="font-sans text-xl text-grey-600 mb-8">
            Find answers to your questions about preserving and managing your digital legacy
          </p>

          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <input
              type="text"
              placeholder="Search for help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-6 py-4 pl-12 border border-grey-300 rounded-2xl font-sans text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-grey-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white border border-grey-200 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="font-display text-lg font-bold text-grey-700 mb-2">Live Chat Support</h3>
            <p className="font-sans text-grey-600 mb-4">Get instant help from our support team</p>
            <button
              onClick={openTawkTo}
              className="px-6 py-2 bg-blue-900 hover:bg-blue-800 text-white rounded-lg transition-colors"
            >
              Start Chat
            </button>
          </div>

          <div className="bg-white border border-grey-200 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="font-display text-lg font-bold text-grey-700 mb-2">Email Support</h3>
            <p className="font-sans text-grey-600 mb-4">Send us a detailed message</p>
            <a
              href="mailto:support@endofanera.ai"
              className="inline-block px-6 py-2 border border-grey-300 text-grey-700 hover:bg-grey-50 rounded-lg transition-colors"
            >
              Send Email
            </a>
          </div>

          <div className="bg-white border border-grey-200 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 bg-orange-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-orange-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="font-display text-lg font-bold text-grey-700 mb-2">User Guide</h3>
            <p className="font-sans text-grey-600 mb-4">Comprehensive documentation</p>
            <button
              onClick={() => scrollToSection('help-articles')}
              className="px-6 py-2 border border-grey-300 text-grey-700 hover:bg-grey-50 rounded-lg transition-colors"
            >
              View Guide
            </button>
          </div>
        </div>

        {/* Features Section */}
        <div id="features" className="mb-16 scroll-mt-20">
          <h2 className="font-display text-3xl font-bold text-grey-700 mb-8 text-center">Platform Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white border border-grey-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-full mb-4 flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-display text-xl font-bold text-grey-700 mb-3">Digital Asset Management</h3>
              <p className="font-sans text-grey-600">
                Organize and preserve all your important digital assets including documents, photos, videos, and accounts in one secure location.
              </p>
            </div>

            <div className="bg-white border border-grey-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-full mb-4 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="font-display text-xl font-bold text-grey-700 mb-3">Family Access Control</h3>
              <p className="font-sans text-grey-600">
                Designate executors and beneficiaries with customizable access permissions to ensure your wishes are carried out properly.
              </p>
            </div>

            <div className="bg-white border border-grey-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-full mb-4 flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="font-display text-xl font-bold text-grey-700 mb-3">Estate Planning Tools</h3>
              <p className="font-sans text-grey-600">
                Create comprehensive estate plans with guided workflows for wills, trusts, healthcare directives, and power of attorney documents.
              </p>
            </div>

            <div className="bg-white border border-grey-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-orange-100 rounded-full mb-4 flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-display text-xl font-bold text-grey-700 mb-3">Automated Backup & Sync</h3>
              <p className="font-sans text-grey-600">
                Continuous automatic backups ensure your digital legacy is always protected and accessible when needed.
              </p>
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div id="security" className="mb-16 scroll-mt-20 bg-gradient-to-br from-blue-50 to-green-50 rounded-3xl p-12">
          <h2 className="font-display text-3xl font-bold text-grey-700 mb-8 text-center">Security & Privacy</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-grey-700 mb-2">AES-256 Encryption</h3>
                  <p className="font-sans text-grey-600">
                    All sensitive data is encrypted using military-grade AES-256 encryption, both in transit and at rest, ensuring maximum security.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-green-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-grey-700 mb-2">Client-Side Encryption</h3>
                  <p className="font-sans text-grey-600">
                    Your data is encrypted on your device before it ever leaves your browser, ensuring that only you have access to your unencrypted information.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-purple-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-grey-700 mb-2">Two-Factor Authentication</h3>
                  <p className="font-sans text-grey-600">
                    Add an extra layer of security to your account with optional two-factor authentication for enhanced protection.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-orange-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-grey-700 mb-2">Privacy by Design</h3>
                  <p className="font-sans text-grey-600">
                    We never sell your data. Our platform is built with privacy as a core principle, ensuring your information stays yours.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Help Categories and Articles */}
        <div id="help-articles" className="scroll-mt-20">
          <h2 className="font-display text-3xl font-bold text-grey-700 mb-8 text-center">Knowledge Base</h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading articles...</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No articles available yet.</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Categories Sidebar */}
              <div className="lg:col-span-1">
                <h3 className="font-display text-xl font-bold text-grey-700 mb-6">Categories</h3>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                        selectedCategory === category
                          ? 'bg-teal-100 text-teal-900 border border-teal-200'
                          : 'text-grey-700 hover:bg-grey-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-sans font-medium capitalize">
                          {category === 'all' ? 'All Topics' : category}
                        </span>
                        <span className="font-sans text-sm text-grey-500">
                          {getCategoryCount(category)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Articles */}
              <div className="lg:col-span-3">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-display text-xl font-bold text-grey-700">
                    {selectedCategory === 'all' ? 'All Articles' : selectedCategory}
                  </h3>
                  <span className="font-sans text-grey-600">{filteredArticles.length} articles</span>
                </div>

                {filteredArticles.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No articles match your search.
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {filteredArticles.map((article) => (
                      <article
                        key={article.id}
                        onClick={() => setSelectedArticle(article)}
                        className="bg-white border border-grey-200 rounded-2xl p-6 hover:shadow-lg hover:border-teal-300 transition-all cursor-pointer group"
                      >
                        <h4 className="font-display text-lg font-bold text-grey-700 mb-2 group-hover:text-teal-700 transition-colors">
                          {article.title}
                        </h4>
                        <p className="font-sans text-grey-600 text-sm mb-3">
                          {getPreview(article.content)}
                        </p>
                        <div className="flex items-center justify-between">
                          {article.tags && article.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {article.tags.slice(0, 2).map((tag, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          <span className="text-teal-600 text-sm font-medium group-hover:text-teal-700">
                            Read →
                          </span>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-grey-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-grey-700 mb-4">Still need help?</h2>
          <p className="font-sans text-lg text-grey-600 mb-8">
            Our digital legacy specialists are here to help you preserve your memories
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* <button
              onClick={() => navigate('/frame-137')}
              className="px-8 py-4 bg-blue-900 hover:bg-blue-800 text-white font-medium rounded-lg transition-colors"
            >
              Schedule Consultation
            </button> */}
            <a
              href="mailto:support@endofanera.ai"
              className="px-8 py-4 border border-grey-300 text-grey-700 hover:bg-grey-50 rounded-lg transition-colors inline-block"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-grey-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-sans text-grey-400">Â© 2024 End of an Era. All rights reserved.</p>
        </div>
      </footer>

      {/* Article Modal */}
      {selectedArticle && (
        <Modal
          open={!!selectedArticle}
          onClose={() => setSelectedArticle(null)}
          title={selectedArticle.title}
        >
          <div className="p-6">
            {selectedArticle.categoryName && (
              <span className="inline-block px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm mb-4">
                {selectedArticle.categoryName}
              </span>
            )}
            <div className="text-gray-700 [&_h1]:text-gray-900 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-gray-900 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-2 [&_h3]:text-gray-900 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_p]:my-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:my-2 [&_li]:my-1 [&_a]:text-teal-600 [&_a]:underline [&_strong]:font-bold [&_strong]:text-gray-900 [&_em]:italic [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:rounded [&_pre]:overflow-x-auto [&_blockquote]:border-l-4 [&_blockquote]:border-teal-500 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:bg-gray-50 [&_blockquote]:py-2 [&_hr]:my-4 [&_hr]:border-gray-300">
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                remarkPlugins={[remarkBreaks]}
              >
                {selectedArticle.content}
              </ReactMarkdown>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={() => setSelectedArticle(null)}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Frame199;
